import { mergeDuplicateCategories } from "./category-merge.js";

const DB_NAME = "mitjapart";
const DB_VERSION = 2;
const REMOTE_API = "/api/state";

const STORES = ["expenses", "recurring", "settlements", "settings"];

let dbPromise;
let remoteStatePromise;
let remoteEnabled = location.hostname !== "localhost" && location.hostname !== "127.0.0.1";

export function openDatabase() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains("expenses")) {
        const store = db.createObjectStore("expenses", { keyPath: "id" });
        store.createIndex("date", "date");
        store.createIndex("category", "category");
        store.createIndex("payer", "payer");
      }

      if (!db.objectStoreNames.contains("recurring")) {
        db.createObjectStore("recurring", { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains("settlements")) {
        const store = db.createObjectStore("settlements", { keyPath: "id" });
        store.createIndex("date", "date");
      }

      if (db.objectStoreNames.contains("budgets")) {
        db.deleteObjectStore("budgets");
      }

      if (!db.objectStoreNames.contains("settings")) {
        db.createObjectStore("settings", { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

export async function getAll(storeName) {
  const remote = await getRemoteState();
  if (remote) return storeName === "settings" ? [remote.data.settings] : remote.data[storeName] || [];
  return getAllLocal(storeName);
}

export async function getOne(storeName, key) {
  const remote = await getRemoteState();
  if (remote) {
    if (storeName === "settings") return key === "main" ? remote.data.settings : undefined;
    return (remote.data[storeName] || []).find((item) => item.id === key);
  }
  return getOneLocal(storeName, key);
}

export async function putOne(storeName, value) {
  if (await mutateRemote({ action: "put", store: storeName, value })) return;
  return putOneLocal(storeName, value);
}

export async function putMany(storeName, values) {
  if (await mutateRemote({ action: "putMany", store: storeName, values })) return;
  return putManyLocal(storeName, values);
}

export async function deleteOne(storeName, key) {
  if (await mutateRemote({ action: "delete", store: storeName, key })) return;
  return deleteOneLocal(storeName, key);
}

export async function clearStore(storeName) {
  if (await mutateRemote({ action: "clearStore", store: storeName })) return;
  return clearStoreLocal(storeName);
}

export async function clearDatabase() {
  if (await mutateRemote({ action: "clear" })) return;
  await Promise.all(STORES.map((store) => clearStoreLocal(store)));
}

export async function replaceDatabase(data) {
  const normalized = normalizeExport(data);
  if (await mutateRemote({ action: "replace", data: normalized })) return;
  return replaceDatabaseLocal(normalized);
}

export async function exportDatabase() {
  const remote = await getRemoteState();
  if (remote) return exportFromState(remote.data);
  const [settings, expenses, recurring, settlements] = await Promise.all([
    getOneLocal("settings", "main"),
    getAllLocal("expenses"),
    getAllLocal("recurring"),
    getAllLocal("settlements"),
  ]);
  return exportFromState({ settings, expenses, recurring, settlements });
}

export async function migrateLocalStorageIfNeeded() {
  const remote = await getRemoteState();
  if (remote?.empty) {
    const localData = await localMigrationData();
    await replaceDatabase(localData || { settings: defaultSettings() });
    return Boolean(localData);
  }

  if (remote) return false;

  const settings = await getOneLocal("settings", "main");
  if (!settings) {
    const localData = await localMigrationData();
    if (!localData) {
      await putOneLocal("settings", defaultSettings());
      return false;
    }

    await replaceDatabaseLocal(localData);
    localStorage.removeItem("mitjapart-state-v2");
    localStorage.removeItem("mitjapart-state-v1");
    return true;
  }

  const localData = {
    settings,
    expenses: await getAllLocal("expenses"),
    recurring: await getAllLocal("recurring"),
    settlements: await getAllLocal("settlements"),
  };
  const merged = mergeDuplicateCategories(localData);
  if (merged.changed) {
    await replaceDatabaseLocal(merged.data);
    return true;
  }

  return false;
}

async function getAllLocal(storeName) {
  const db = await openDatabase();
  return requestToPromise(db.transaction(storeName).objectStore(storeName).getAll());
}

async function getOneLocal(storeName, key) {
  const db = await openDatabase();
  return requestToPromise(db.transaction(storeName).objectStore(storeName).get(key));
}

async function putOneLocal(storeName, value) {
  const db = await openDatabase();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).put(value);
  return transactionDone(tx);
}

async function putManyLocal(storeName, values) {
  const db = await openDatabase();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  values.forEach((value) => store.put(value));
  return transactionDone(tx);
}

async function deleteOneLocal(storeName, key) {
  const db = await openDatabase();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).delete(key);
  return transactionDone(tx);
}

async function clearStoreLocal(storeName) {
  const db = await openDatabase();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).clear();
  return transactionDone(tx);
}

async function replaceDatabaseLocal(data) {
  const db = await openDatabase();
  const tx = db.transaction(STORES, "readwrite");

  STORES.forEach((storeName) => tx.objectStore(storeName).clear());
  (data.expenses || []).forEach((item) => tx.objectStore("expenses").put(item));
  (data.recurring || []).forEach((item) => tx.objectStore("recurring").put(item));
  (data.settlements || []).forEach((item) => tx.objectStore("settlements").put(item));
  tx.objectStore("settings").put(data.settings || defaultSettings());

  return transactionDone(tx);
}

export function isRemoteAvailable() {
  return remoteEnabled;
}

async function getRemoteState() {
  if (!remoteEnabled) return null;
  if (!remoteStatePromise) {
    remoteStatePromise = fetch(REMOTE_API, { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error("Remote database unavailable");
        const payload = await response.json();
        return { empty: Boolean(payload.empty), data: normalizeRemoteState(payload.data) };
      })
      .catch((error) => {
        console.warn(error);
        remoteEnabled = false;
        return null;
      });
  }
  return remoteStatePromise;
}

async function mutateRemote(payload) {
  if (!remoteEnabled) return false;
  try {
    const response = await fetch(REMOTE_API, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error("Remote database mutation failed");
    const result = await response.json();
    remoteStatePromise = Promise.resolve({ empty: Boolean(result.empty), data: normalizeRemoteState(result.data) });
    return true;
  } catch (error) {
    console.warn(error);
    remoteEnabled = false;
    remoteStatePromise = null;
    return false;
  }
}

async function localMigrationData() {
  const raw = localStorage.getItem("mitjapart-state-v2") || localStorage.getItem("mitjapart-state-v1");

  if (raw) {
    try {
      return normalizeExport(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  try {
    const settings = await getOneLocal("settings", "main");
    if (!settings) return null;
    const [expenses, recurring, settlements] = await Promise.all([
      getAllLocal("expenses"),
      getAllLocal("recurring"),
      getAllLocal("settlements"),
    ]);
    if (!expenses.length && !recurring.length && !settlements.length) return null;
    return normalizeExport({ settings, expenses, recurring, settlements });
  } catch {
    return null;
  }
}

function normalizeExport(data = {}) {
  const base = {
    settings: {
      ...defaultSettings(),
      ...(data.settings || {}),
      partners: data.partners || data.settings?.partners || defaultSettings().partners,
      theme: data.settings?.theme || data.theme || "light",
      categories: data.settings?.categories || [],
      id: "main",
    },
    expenses: Array.isArray(data.expenses) ? data.expenses : [],
    recurring: Array.isArray(data.recurring) ? data.recurring : [],
    settlements: Array.isArray(data.settlements) ? data.settlements : [],
  };
  return mergeDuplicateCategories(base).data;
}

function normalizeRemoteState(data = {}) {
  const base = {
    settings: { ...defaultSettings(), ...(data.settings || {}), id: "main" },
    expenses: Array.isArray(data.expenses) ? data.expenses : [],
    recurring: Array.isArray(data.recurring) ? data.recurring : [],
    settlements: Array.isArray(data.settlements) ? data.settlements : [],
  };
  return mergeDuplicateCategories(base).data;
}

function exportFromState(data) {
  const state = normalizeRemoteState(data);
  return {
    version: 3,
    exportedAt: new Date().toISOString(),
    partners: state.settings.partners,
    settings: { theme: state.settings.theme, categories: state.settings.categories || [] },
    expenses: state.expenses,
    recurring: state.recurring,
    settlements: state.settlements,
  };
}

function defaultSettings() {
  return {
    id: "main",
    partners: ["Persona 1", "Persona 2"],
    theme: "light",
    categories: [],
    migratedFromLocalStorage: false,
  };
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
